import { useEffect, useRef, useState } from "react";
import { ExternalLink, Navigation, X } from "lucide-react";
import { LiveTripMap } from "@/components/LiveTripMap";
import { geocodeAddress, routeInfo } from "@/lib/maps";
import { keepAwake, letSleep } from "@/lib/wakeLock";

const ETA_MS = 60000;

const driveTime = (minutes) => {
  const total = Math.round(minutes);
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  return `${hours} h ${String(total % 60).padStart(2, "0")} min`;
};

/** The route to the next stop, INSIDE the chauffeur app.
 *
 *  Switching to the Google Maps app backgrounds this one, and a backgrounded browser stops reporting
 *  GPS — which is how dispatch and the passenger lost the car mid-trip. Keeping the map on this
 *  screen keeps the location feed alive. The map and the route line are drawn from our own server,
 *  so no browser map key is involved.
 */
export const DriverNavigate = ({ open, onClose, destination, label, from }) => {
  const [me, setMe] = useState(from || null);
  const [eta, setEta] = useState(null);
  const [placed, setPlaced] = useState(null);
  const watch = useRef(null);
  const meRef = useRef(from || null);
  // A trip typed without picking an address suggestion has no coordinates — place it once.
  const target = destination?.lat != null ? destination : placed;

  useEffect(() => { if (from?.lat != null) setMe(from); }, [from]);
  useEffect(() => { meRef.current = me; }, [me]);

  useEffect(() => {
    if (!open || destination?.lat != null || !destination?.address) return;
    const address = destination.address;
    let live = true;
    geocodeAddress(address)
      .then((found) => { if (live && found) setPlaced({ address, ...found }); });
    // eslint-disable-next-line consistent-return
    return () => { live = false; };
  }, [open, destination?.lat, destination?.address]);

  useEffect(() => {
    if (!open) return undefined;
    keepAwake();
    const stop = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", stop);
    if (navigator.geolocation) {
      watch.current = navigator.geolocation.watchPosition(
        (pos) => setMe({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000 },
      );
    }
    return () => {
      document.removeEventListener("keydown", stop);
      if (watch.current != null) navigator.geolocation.clearWatch(watch.current);
      watch.current = null;
      letSleep();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Distance and drive time, refreshed on a timer rather than on every GPS ping. The position is
  // read from a ref so a later GPS fix is used without restarting the timer.
  const located = me?.lat != null;
  useEffect(() => {
    if (!open || !located || target?.lat == null) return undefined;
    let live = true;
    const fetchEta = () => routeInfo([meRef.current, target])
      .then((i) => { if (live && i) setEta(i); });
    fetchEta();
    const timer = setInterval(fetchEta, ETA_MS);
    return () => { live = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, located, target?.lat, target?.lng]);

  if (!open) return null;

  const external = target?.lat != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${target.lat},${target.lng}&travelmode=driving`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination?.address || "")}&travelmode=driving`;

  return (
    <div data-testid="driver-navigate-overlay" className="fixed inset-0 z-[60] flex flex-col bg-ink-900">
      <div className="flex items-center justify-between gap-3 border-b border-ink-600 px-4 py-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Driving to</p>
          <p className="truncate text-sm text-slate-100">{label || destination.address}</p>
          <p data-testid="driver-navigate-eta" className="font-mono text-[11px] text-slate-500">
            {eta?.minutes != null
              ? `${driveTime(eta.minutes)} · ${eta.miles?.toFixed?.(1) ?? "–"} mi away`
              : located ? "Measuring the route…" : "Waiting for your location…"}
          </p>
        </div>
        <button data-testid="driver-navigate-close" onClick={onClose} aria-label="Close navigation"
          className="shrink-0 rounded-full border border-ink-600 p-2 text-slate-300 hover:text-gold">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 p-3">
        <LiveTripMap driver={me} target={target} label={label || "Destination"}
          heightClass="h-full" testId="driver-navigate-map" />
      </div>

      <div className="border-t border-ink-600 px-4 py-3">
        <p className="text-[11px] leading-relaxed text-slate-500">
          Keep this screen open and dispatch and your passenger keep seeing you. Opening the Google
          Maps app instead pauses your location until you come back.
        </p>
        <a data-testid="driver-navigate-external" href={external} target="_blank" rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-gold">
          <ExternalLink className="h-3.5 w-3.5" /> Open in the Google Maps app anyway
        </a>
      </div>
    </div>
  );
};

export const NavigateButton = ({ onClick, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className="flex h-12 items-center justify-center gap-2 rounded-xl border border-ink-600 bg-ink-900 text-sm font-semibold text-slate-100 active:scale-[0.99]">
    <Navigation className="h-4 w-4 text-gold" /> Navigate
  </button>
);
